import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { popupFactory } from '@/factories'
import { canReloadNow, markReloaded } from '@/utils'

import { useBuildVersionStore } from './build-version.store'
import { isReloadBlockedByDirtyState } from './use-block-reload-when-dirty'

const HIDDEN_THRESHOLD_MS = 5 * 60 * 1000

const attemptSafeReload = () => {
  const { isStale } = useBuildVersionStore.getState()
  if (!isStale) return
  if (popupFactory.getCurrentPopup() !== null) return
  if (isReloadBlockedByDirtyState()) return
  if (!canReloadNow()) return

  markReloaded()
  window.location.reload()
}

/**
 * Tier 1 — silent reload, attempted only at moments judged safe: a route change, or the
 * tab regaining visibility after being hidden a while. Gated on no popup being open and
 * no form having opted into `useBlockReloadWhenDirty`. See EL-13298.
 */
export const useSafeReload = () => {
  const location = useLocation()
  const hiddenAtRef = useRef<number | null>(null)

  useEffect(() => {
    attemptSafeReload()
  }, [location.pathname])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now()
        return
      }

      const hiddenAt = hiddenAtRef.current
      hiddenAtRef.current = null
      if (hiddenAt && Date.now() - hiddenAt >= HIDDEN_THRESHOLD_MS) {
        attemptSafeReload()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])
}
